// Mark that main.js has started loading
console.log('main.js script starting to load');
window.mainJsLoaded = true;

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { ParametricGeometry } from 'three/examples/jsm/geometries/ParametricGeometry';

console.log('Three.js version:', THREE.REVISION);
// Make sure THREE is available globally for debugging
window.THREE = THREE;

// Shader code for point clouds
const vertexShader = `
uniform float trackOffset;
uniform float waveFrequency; // Controls how many waves appear around the strip
uniform mat4 rotationMatrix;
uniform float pointSize;

attribute vec3 offset;
attribute float intensity;
attribute float positionV;
attribute float baseU;

varying float vIntensity;
varying float vColor; // 0 = primary color, 1 = secondary color

// Möbius strip parametric function
vec3 getMobiusPosition(float u, float v) {
    float phi = u;
    float t = (v - 0.5) * 2.0;
    float R = 5.0;
    float w = 1.0;
    float x = (R + w * t * cos(phi / 2.0)) * cos(phi);
    float y = (R + w * t * cos(phi / 2.0)) * sin(phi);
    float z = w * t * sin(phi / 2.0);
    return vec3(x, y, z);
}

// Calculate normal at a point on the Möbius strip
vec3 getMobiusNormal(float u, float v) {
    float phi = u;
    float t = (v - 0.5) * 2.0;
    float R = 5.0;
    float w = 1.0;
    
    // Partial derivatives simplified
    float dxdu = -R * sin(phi) - w * t * (0.5 * sin(phi/2.0) * cos(phi) + cos(phi/2.0) * sin(phi));
    float dydu = R * cos(phi) + w * t * (0.5 * sin(phi/2.0) * sin(phi) - cos(phi/2.0) * cos(phi));
    float dzdu = 0.5 * w * t * cos(phi/2.0);
    vec3 du = vec3(dxdu, dydu, dzdu);
    
    float dxdv = 2.0 * w * cos(phi/2.0) * cos(phi);
    float dydv = 2.0 * w * cos(phi/2.0) * sin(phi);
    float dzdv = 2.0 * w * sin(phi/2.0);
    vec3 dv = vec3(dxdv, dydv, dzdv);
    
    return normalize(cross(du, dv));
}

void main() {
    // Get position and normal for movement
    float u = mod(baseU + trackOffset * 2.0 * 3.14159, 2.0 * 3.14159);
    vec3 basePosition = getMobiusPosition(u, positionV);
    vec3 normal = getMobiusNormal(u, positionV);
    vec3 finalPosition = basePosition + offset;
    
    // Determine which side of the strip we're on (for opposite colors on opposite sides)
    float side = sign(dot(normal, offset));
    
    // Create wave effect based purely on position along strip (u parameter)
    // The wave is now directly tied to the point's position
    float positionFactor = u / (2.0 * 3.14159);
    float wave = mod(positionFactor * waveFrequency, 1.0);
    
    // Apply smoothstep for smoother transitions
    wave = smoothstep(0.0, 1.0, wave);
    
    // If on opposite side, use opposite color
    if (side < 0.0) {
        wave = 1.0 - wave;
    }
    
    // Set color value (0 = primary color, 1 = secondary color)
    vColor = wave;
    
    // Standard transformations
    vec4 rotatedPosition = rotationMatrix * vec4(finalPosition, 1.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(rotatedPosition.xyz, 1.0);
    
    // Size attenuation
    float distanceFactor = clamp(1.0 / -modelViewMatrix[2].z, 0.0, 1.0);
    gl_PointSize = pointSize * mix(0.5, 2.0, distanceFactor);
    
    // Pass intensity
    vIntensity = intensity;
}
`;

const fragmentShader = `
uniform float opacity;
uniform vec3 pointColor;
uniform vec3 secondaryColor;

varying float vIntensity;
varying float vColor; // Now a continuous value between 0.0 and 1.0

void main() {
    // Create soft circular point shape
    float d = length(gl_PointCoord.xy - vec2(0.5, 0.5)) * 2.0;
    if (d > 1.0) discard; // Discard pixels outside the circle
    
    // Smooth edge falloff
    float falloff = 1.0 - smoothstep(0.6, 1.0, d);
    
    // Apply glow effect based on intensity
    float glowIntensity = pow(vIntensity * falloff, 1.6);
    
    // Mix colors based on the vColor value
    vec3 finalColor = mix(pointColor, secondaryColor, vColor);
    
    // Set final fragment color with appropriate opacity
    gl_FragColor = vec4(finalColor * glowIntensity, opacity * falloff);
}
`;

class MobiusStrip {
    constructor() {
        console.log('Initializing MobiusStrip...');
        
        // Create scene with a dark background
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x0a0a0a);

        // Add animation state
        this.isAnimating = true;

        // Light cloud configuration
        this.lightCloudConfig = {
            numPoints: 5000,         // Reduced from 50000 to 2000 points per cloud
            spreadRadius: 0.15,      // How far points spread from center
            pointSize: 15.0,          // More reasonable default point size
            opacity: 0.4,           // Reduced opacity to minimize harsh overlap effects
            densityFalloff: 0.5,     // Reduced falloff for better distribution
            intensityFalloff: 0.8,   // Increased falloff for less glow spread
            color: new THREE.Color(0xe1e9f0),  // Muted grey color
            intensity: 0.8           // Reduced intensity to further minimize harshness
        };

        // Add light clouds
        this.lightClouds = [];
        this.lightCloudPaths = [];
        this.pointOffsets = [];      // Store random offsets for each point
        this.pointIntensities = [];  // Store intensity values for each point

        // Initialize track movement offsets
        this.trackOffset = 0;
        this.sphereOffset = 0;       // Separate offset for spheres moving at 1.5x speed

        // Set up camera with better initial position
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        this.camera.position.set(0, 5, 10);
        this.camera.lookAt(0, 0, 0);

        // Set up renderer with proper settings
        const canvas = document.querySelector('#mobius-canvas');
        if (!canvas) {
            console.error('Canvas element not found!');
            return;
        }

        this.renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true,
            alpha: true
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;

        // Set up CSS2D renderer for labels
        this.labelRenderer = new CSS2DRenderer();
        this.labelRenderer.setSize(window.innerWidth, window.innerHeight);
        this.labelRenderer.domElement.style.position = 'absolute';
        this.labelRenderer.domElement.style.top = '0px';
        this.labelRenderer.domElement.style.pointerEvents = 'none';
        document.querySelector('.container').appendChild(this.labelRenderer.domElement);

        // Set up controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.minDistance = 5;
        this.controls.maxDistance = 20;

        this.mobiusStrip = null;
        this.interactiveObjects = [];
        this.hoverLabel = document.getElementById('hover-label');
        this.time = 0;

        // Create shared shader uniforms
        this.shaderUniforms = {
            trackOffset: { value: 0.0 },
            waveFrequency: { value: 2.0 },     // How many waves appear around the strip
            rotationMatrix: { value: new THREE.Matrix4() },
            pointSize: { value: this.lightCloudConfig.pointSize },
            opacity: { value: this.lightCloudConfig.opacity },
            pointColor: { value: new THREE.Vector3(0.882, 0.914, 0.941) },  // Muted grey color
            secondaryColor: { value: new THREE.Vector3(0.12, 0.12, 0.12) }  // Rich Black as default
        };
        
        // Alternative muted/pastel neon blue colors to try (comment out current color and try these)
        this.neonBlueOptions = [
            // Current vibrant neon blue
            { name: "Vibrant Neon Blue", rgb: new THREE.Vector3(0.2, 0.7, 1.0), hex: "#33B3FF" },
            
            // Muted/Pastel Options
            { name: "Powder Blue", rgb: new THREE.Vector3(0.4, 0.8, 0.94), hex: "#66CCEF" },
            { name: "Azure Mist", rgb: new THREE.Vector3(0.5, 0.7, 0.9), hex: "#80B3E6" },
            { name: "Pastel Cerulean", rgb: new THREE.Vector3(0.45, 0.65, 0.85), hex: "#73A6D9" },
            { name: "Baby Blue", rgb: new THREE.Vector3(0.54, 0.81, 0.94), hex: "#8ACEF0" },
            { name: "Sky Blue", rgb: new THREE.Vector3(0.53, 0.81, 0.98), hex: "#87CEFA" },
            { name: "Electric Blue (Muted)", rgb: new THREE.Vector3(0.4, 0.6, 0.9), hex: "#6699E6" }, 
            { name: "Cornflower Blue", rgb: new THREE.Vector3(0.39, 0.58, 0.93), hex: "#6495ED" },
            { name: "Periwinkle Blue", rgb: new THREE.Vector3(0.55, 0.55, 0.82), hex: "#8C8CD1" },
            { name: "Cool Blue", rgb: new THREE.Vector3(0.35, 0.75, 0.85), hex: "#59BFD9" },
            { name: "Aquamarine Pastel", rgb: new THREE.Vector3(0.5, 0.8, 0.8), hex: "#80CCCC" },
            { name: "Teal Pastel", rgb: new THREE.Vector3(0.35, 0.75, 0.75), hex: "#59BFBF" },
            { name: "Carolina Blue", rgb: new THREE.Vector3(0.6, 0.73, 0.89), hex: "#99BAE3" },
            { name: "Robin's Egg Blue", rgb: new THREE.Vector3(0.5, 0.8, 0.85), hex: "#80CCD9" }
        ];

        // Darker, more muted blue options
        this.darkBlueOptions = [
            { name: "Midnight Blue (Muted)", rgb: new THREE.Vector3(0.15, 0.25, 0.35), hex: "#264059" },
            { name: "Deep Navy (Muted)", rgb: new THREE.Vector3(0.12, 0.22, 0.32), hex: "#1F3852" },
            { name: "Dark Slate Blue", rgb: new THREE.Vector3(0.18, 0.28, 0.38), hex: "#2E4761" },
            { name: "Charcoal Blue", rgb: new THREE.Vector3(0.14, 0.24, 0.34), hex: "#243D56" },
            { name: "Deep Ocean Blue", rgb: new THREE.Vector3(0.16, 0.26, 0.36), hex: "#29425C" },
            { name: "Dark Steel Blue", rgb: new THREE.Vector3(0.13, 0.23, 0.33), hex: "#213B54" },
            { name: "Twilight Blue", rgb: new THREE.Vector3(0.17, 0.27, 0.37), hex: "#2B445E" },
            { name: "Deep Space Blue", rgb: new THREE.Vector3(0.11, 0.21, 0.31), hex: "#1C3650" },
            { name: "Dark Denim Blue", rgb: new THREE.Vector3(0.19, 0.29, 0.39), hex: "#314B63" },
            { name: "Shadow Blue", rgb: new THREE.Vector3(0.12, 0.22, 0.32), hex: "#1F3852" }
        ];

        // Dark grey options
        this.darkGreyOptions = [
            { name: "Rich Black", rgb: new THREE.Vector3(0.12, 0.12, 0.12), hex: "#1F1F1F" },
            { name: "Charcoal", rgb: new THREE.Vector3(0.15, 0.15, 0.15), hex: "#262626" },
            { name: "Dark Slate", rgb: new THREE.Vector3(0.13, 0.13, 0.13), hex: "#212121" },
            { name: "Deep Grey", rgb: new THREE.Vector3(0.14, 0.14, 0.14), hex: "#242424" },
            { name: "Carbon Grey", rgb: new THREE.Vector3(0.11, 0.11, 0.11), hex: "#1C1C1C" },
            { name: "Dark Graphite", rgb: new THREE.Vector3(0.16, 0.16, 0.16), hex: "#292929" },
            { name: "Shadow Grey", rgb: new THREE.Vector3(0.13, 0.13, 0.13), hex: "#212121" },
            { name: "Deep Charcoal", rgb: new THREE.Vector3(0.14, 0.14, 0.14), hex: "#242424" },
            { name: "Dark Steel", rgb: new THREE.Vector3(0.15, 0.15, 0.15), hex: "#262626" },
            { name: "Night Grey", rgb: new THREE.Vector3(0.12, 0.12, 0.12), hex: "#1F1F1F" }
        ];
        
        // To try a different color, use one of these:
        // For example: this.shaderUniforms.secondaryColor.value = this.neonBlueOptions[3].rgb;
        // For dark colors: this.shaderUniforms.secondaryColor.value = this.darkBlueOptions[0].rgb;
        // For dark greys: this.shaderUniforms.secondaryColor.value = this.darkGreyOptions[0].rgb;

        // Add wave control parameters
        this.waveConfig = {
            frequency: 2.0,       // Initial frequency (waves per cycle)
            trackSpeedFactor: 1.0 // Multiplier for track speed to adjust wave speed
        };

        this.init();
        this.setupEventListeners();
        this.animate();
    }

    init() {
        console.log('Initializing scene...');
        
        // Create Möbius strip using parametric geometry
        const mobius = (u, v, target) => {
            const phi = u * 2 * Math.PI;
            const t = (v - 0.5) * 2;
            const R = 5;
            const w = 1;
            const x = (R + w * t * Math.cos(phi / 2)) * Math.cos(phi);
            const y = (R + w * t * Math.cos(phi / 2)) * Math.sin(phi);
            const z = w * t * Math.sin(phi / 2);
            target.set(x, y, z);
        };

        const geometry = new ParametricGeometry(mobius, 100, 20);
        
        // Create a subtle noise texture
        const textureSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = textureSize;
        canvas.height = textureSize;
        const ctx = canvas.getContext('2d');
        
        // Fill with base color
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, textureSize, textureSize);
        
        // Add subtle noise
        const imageData = ctx.getImageData(0, 0, textureSize, textureSize);
        const data = imageData.data;
        
        for (let i = 0; i < data.length; i += 4) {
            const noise = Math.random() * 0.15; // Very subtle noise
            data[i] = data[i + 1] = data[i + 2] = Math.floor(noise * 255);
            data[i + 3] = 255;
        }
        
        ctx.putImageData(imageData, 0, 0);
        
        // Add very subtle gradient
        const gradient = ctx.createLinearGradient(0, 0, textureSize, textureSize);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.03)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.01)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.03)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, textureSize, textureSize);
        
        // Create texture from canvas
        const texture = new THREE.CanvasTexture(canvas);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(4, 1); // Reduced repeat for more subtle pattern
        
        const material = new THREE.MeshPhongMaterial({
            map: texture,
            side: THREE.DoubleSide,
            shininess: 100,
            emissive: 0x111111,
            emissiveIntensity: 0.1 // Reduced emissive intensity
        });
        
        this.mobiusStrip = new THREE.Mesh(geometry, material);
        this.scene.add(this.mobiusStrip);

        // Create track lines and light clouds
        this.trackLines = [];
        const numLines = 40;
        const lineGeometry = new THREE.BufferGeometry();
        const lineMaterial = new THREE.LineBasicMaterial({ 
            color: 0xcccccc,
            transparent: true,
            opacity: 0.6,
            linewidth: 2
        });
        
        // Create glow material for the lines
        const glowMaterial = new THREE.LineBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.4,
            linewidth: 8
        });
        
        // Create outer glow material for bloom effect
        const outerGlowMaterial = new THREE.LineBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.15,
            linewidth: 12
        });
        
        // Create points for all lines
        const points = [];
        
        for (let i = 0; i < numLines; i++) {
            const u = (i * 2 * Math.PI / numLines) % (2 * Math.PI);
            const v = 0.5;
            
            // Get center point of the line
            const centerPos = this.getMobiusPosition(u, v);
            
            // Calculate tangent vector at this point (along the track)
            const tangent = new THREE.Vector3();
            const epsilon = 0.01;
            const nextU = (u + epsilon) % (2 * Math.PI);
            const nextPos = this.getMobiusPosition(nextU, v);
            tangent.subVectors(nextPos, centerPos).normalize();
            
            // Calculate surface normal at this point
            const normal = new THREE.Vector3();
            const vEpsilon = 0.01;
            const nextV = v + vEpsilon;
            const nextVPos = this.getMobiusPosition(u, nextV);
            const vTangent = new THREE.Vector3().subVectors(nextVPos, centerPos).normalize();
            normal.crossVectors(tangent, vTangent).normalize();
            
            // Calculate perpendicular vector in the surface plane
            const perpendicular = new THREE.Vector3();
            perpendicular.crossVectors(normal, tangent).normalize();
            
            // Create two points for the line along the surface, extending to the edges
            const startPos = this.getMobiusPosition(u, 0); // Edge at v = 0
            const endPos = this.getMobiusPosition(u, 1);   // Edge at v = 1
            
            points.push(startPos.x, startPos.y, startPos.z);
            points.push(endPos.x, endPos.y, endPos.z);

            // Store the path for each light cloud
            this.lightCloudPaths.push([startPos, endPos]);
        }
        
        lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
        lineGeometry.computeBoundingSphere();
        
        // Create the main line object
        const line = new THREE.LineSegments(lineGeometry, lineMaterial);
        this.trackLines.push(line);
        this.scene.add(line);
        
        // Create the glow line object
        const glowLine = new THREE.LineSegments(lineGeometry.clone(), glowMaterial);
        this.trackLines.push(glowLine);
        this.scene.add(glowLine);
        
        // Create the outer glow line object
        const outerGlowLine = new THREE.LineSegments(lineGeometry.clone(), outerGlowMaterial);
        this.trackLines.push(outerGlowLine);
        this.scene.add(outerGlowLine);

        // Create light clouds for all lines
        this.createLightClouds();

        // Enhanced lighting setup
        const ambientLight = new THREE.AmbientLight(0x404040);
        this.scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(10, 10, 10);
        this.scene.add(directionalLight);

        // Add interactive objects
        this.addInteractiveObjects();
        
        console.log('Scene initialized successfully');
    }

    addInteractiveObjects() {
        const pages = [
            { label: 'About', url: '/about' },
            { label: 'Portfolio', url: '/portfolio' },
            { label: 'Explore', url: '/explore' }
        ];

        const sphereGeometry = new THREE.SphereGeometry(0.8, 32, 32);
        
        // Create a cube texture for better reflections
        const cubeTextureLoader = new THREE.CubeTextureLoader();
        const envMap = cubeTextureLoader.load([
            'https://threejs.org/examples/textures/cube/SwedishRoyalCastle/px.jpg', // right
            'https://threejs.org/examples/textures/cube/SwedishRoyalCastle/nx.jpg', // left
            'https://threejs.org/examples/textures/cube/SwedishRoyalCastle/py.jpg', // top
            'https://threejs.org/examples/textures/cube/SwedishRoyalCastle/ny.jpg', // bottom
            'https://threejs.org/examples/textures/cube/SwedishRoyalCastle/pz.jpg', // front
            'https://threejs.org/examples/textures/cube/SwedishRoyalCastle/nz.jpg'  // back
        ]);
        
        pages.forEach((page, index) => {
            // Create enhanced mirrored material
            const sphereMaterial = new THREE.MeshStandardMaterial({
                color: 0x111111,  // Slightly lighter black
                metalness: 1.0,
                roughness: 0.0,   // Perfectly smooth for maximum reflection
                envMap: envMap,
                envMapIntensity: 3.0,  // Increased reflection intensity
                side: THREE.DoubleSide,
                emissive: 0x111111,
                emissiveIntensity: 0.2  // Subtle self-glow
            });
            
            const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            sphere.userData = page;
            this.interactiveObjects.push(sphere);
            this.scene.add(sphere);

            // Add CSS2D label
            const div = document.createElement('div');
            div.className = 'label';
            div.textContent = page.label;
            div.style.marginTop = '-1em';
            const label = new CSS2DObject(div);
            label.position.set(0, 0.5, 0);
            sphere.add(label);
            label.visible = false;
        });
    }

    getMobiusPosition(u, v) {
        const phi = u;
        const t = (v - 0.5) * 2;
        const R = 5;
        const w = 1;
        const x = (R + w * t * Math.cos(phi / 2)) * Math.cos(phi);
        const y = (R + w * t * Math.cos(phi / 2)) * Math.sin(phi);
        const z = w * t * Math.sin(phi / 2);
        return new THREE.Vector3(x, y, z);
    }

    // Test different point sizes with a visual comparison
    testPointSizes() {
        const sizes = [1, 2, 3, 5, 10, 15, 20];
        
        let currentIndex = 0;
        
        const showNextSize = () => {
            if (currentIndex >= sizes.length) {
                console.log('Point size test complete. Restoring original size of 3.0');
                this.setPointSize(3.0);
                return;
            }
            
            const size = sizes[currentIndex];
            this.setPointSize(size);
            console.log(`Testing point size: ${size}`);
            
            currentIndex++;
            setTimeout(showNextSize, 1500); // Show each size for 1.5 seconds
        };
        
        showNextSize();
    }

    // Method to manually set point size
    setPointSize(size) {
        // Update the config
        this.lightCloudConfig.pointSize = size;
        
        // Update the shader uniform (this is what actually affects the rendering)
        if (this.shaderUniforms && this.shaderUniforms.pointSize) {
            this.shaderUniforms.pointSize.value = size;
            this.shaderUniforms.pointSize.needsUpdate = true;
        }
        
        console.log(`Point size updated to: ${size}`);
        
        // Force a render to see the change immediately
        this.renderer.render(this.scene, this.camera);
    }

    setupEventListeners() {
        window.addEventListener('resize', () => this.onWindowResize());
        window.addEventListener('mousemove', (event) => this.onMouseMove(event));
        window.addEventListener('click', (event) => this.onClick(event));
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.labelRenderer.setSize(window.innerWidth, window.innerHeight);
    }

    onMouseMove(event) {
        const mouse = new THREE.Vector2();
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, this.camera);

        const intersects = raycaster.intersectObjects(this.interactiveObjects);
        
        this.interactiveObjects.forEach(sphere => {
            sphere.material.metalness = 1.0;
            sphere.material.roughness = 0.1;
            sphere.material.emissiveIntensity = 0.2;
            if (sphere.children[0]) sphere.children[0].visible = false;
        });

        if (intersects.length > 0) {
            const hoveredSphere = intersects[0].object;
            hoveredSphere.material.metalness = 0.9;
            hoveredSphere.material.roughness = 0.2;
            hoveredSphere.material.emissiveIntensity = 0.4;  // Increased glow on hover
            hoveredSphere.children[0].visible = true;
            
            // Pause animation when hovering over a sphere
            this.isAnimating = false;
        } else {
            // Resume animation when not hovering
            this.isAnimating = true;
        }
    }

    onClick(event) {
        const mouse = new THREE.Vector2();
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, this.camera);

        const intersects = raycaster.intersectObjects(this.interactiveObjects);
        if (intersects.length > 0) {
            const clickedSphere = intersects[0].object;
            window.location.href = clickedSphere.userData.url;
        }
    }

    createLightClouds() {
        // Create a light cloud for each track line
        for (let i = 0; i < this.lightCloudPaths.length; i++) {
            const baseU = (i * 2 * Math.PI / this.lightCloudPaths.length) % (2 * Math.PI);
            this.createLightCloud(baseU);
        }
    }

    createLightCloud(baseU) {
        // Arrays to store attribute data
        const positions = [];   // Initial positions (can be all zeros)
        const offsets = [];     // Random offsets from track
        const intensities = []; // Intensity values (for glow)
        const positionVs = [];  // V parameter values
        const baseUs = [];      // U parameter base values
        
        // Generate points with varying density
        let validPointCount = 0;
        
        for (let i = 0; i < this.lightCloudConfig.numPoints; i++) {
            // Calculate position along the track (v from 0 to 1)
            const v = i / (this.lightCloudConfig.numPoints - 1);
            
            // Generate random offset
            const offsetX = (Math.random() - 0.5) * this.lightCloudConfig.spreadRadius;
            const offsetY = (Math.random() - 0.5) * this.lightCloudConfig.spreadRadius;
            const offsetZ = (Math.random() - 0.5) * this.lightCloudConfig.spreadRadius;
            
            // Calculate distance from center for density falloff
            const distanceFromCenter = Math.sqrt(offsetX*offsetX + offsetY*offsetY + offsetZ*offsetZ);
            const densityFactor = Math.exp(-distanceFromCenter * this.lightCloudConfig.densityFalloff);
            
            // Only place point if it passes density check
            if (Math.random() < densityFactor) {
                // Store initial position (will be ignored by shader)
                positions.push(0, 0, 0);
                
                // Store attributes for the shader
                offsets.push(offsetX, offsetY, offsetZ);
                
                // Store v parameter and base u with small random variation
                // This helps stagger the color transition within a cloud
                const baseUVariation = (Math.random() - 0.5) * 0.1; // Small random offset ±0.05
                positionVs.push(v);
                baseUs.push(baseU + baseUVariation);
                
                // Store intensity based on distance from center
                const intensity = Math.exp(-distanceFromCenter * this.lightCloudConfig.intensityFalloff) 
                                 * this.lightCloudConfig.intensity;
                intensities.push(intensity);
                
                validPointCount++;
            }
        }
        
        // Create buffer geometry with the actual number of valid points
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('offset', new THREE.Float32BufferAttribute(offsets, 3));
        geometry.setAttribute('intensity', new THREE.Float32BufferAttribute(intensities, 1));
        geometry.setAttribute('positionV', new THREE.Float32BufferAttribute(positionVs, 1));
        geometry.setAttribute('baseU', new THREE.Float32BufferAttribute(baseUs, 1));
        
        // Create shader material
        const material = new THREE.ShaderMaterial({
            uniforms: this.shaderUniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            transparent: true,
            blending: THREE.NormalBlending,
            depthWrite: false,
        });
        
        // Create points object
        const lightCloud = new THREE.Points(geometry, material);
        this.lightClouds.push(lightCloud);
        this.scene.add(lightCloud);
    }

    animate() {
        requestAnimationFrame(() => this.animate());

        const delta = 0.005;
        this.time += delta;

        if (this.isAnimating) {
            // Rotate Möbius strip (global rotation)
            this.mobiusStrip.rotation.y += 0.005;

            // Update texture offset to move with the track
            if (this.mobiusStrip.material.map) {
                this.mobiusStrip.material.map.offset.x = this.trackOffset;
            }

            // Update track movement - this now controls both point movement and color wave
            this.trackOffset += 0.001 * this.waveConfig.trackSpeedFactor;
            this.sphereOffset += 0.0015; // 1.5x faster than track lines
            
            // Update sphere positions along the strip
            this.interactiveObjects.forEach((sphere, index) => {
                const baseU = (index * (2 * Math.PI / this.interactiveObjects.length)) % (2 * Math.PI);
                const u = (baseU + this.sphereOffset * 2 * Math.PI) % (2 * Math.PI);
                const v = 0.5;
                
                const position = this.getMobiusPosition(u, v);
                position.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.mobiusStrip.rotation.y);
                sphere.position.copy(position);
            });
            
            // Update shader uniforms for light clouds
            this.shaderUniforms.trackOffset.value = this.trackOffset;
            this.shaderUniforms.rotationMatrix.value.makeRotationY(this.mobiusStrip.rotation.y);
        }

        // Move track lines with old method since that's working fine
        if (this.trackLines.length > 0) {
            const rotationMatrix = new THREE.Matrix4().makeRotationY(this.mobiusStrip.rotation.y);
            const line = this.trackLines[0];
            const glowLine = this.trackLines[1];
            const outerGlowLine = this.trackLines[2];
            const positions = line.geometry.attributes.position.array;
            const glowPositions = glowLine.geometry.attributes.position.array;
            const outerGlowPositions = outerGlowLine.geometry.attributes.position.array;
            
            // Update each point's position
            for (let i = 0; i < positions.length; i += 6) {
                const baseU = (i / 6 * 2 * Math.PI / (positions.length / 6)) % (2 * Math.PI);
                const u = (baseU + this.trackOffset * 2 * Math.PI) % (2 * Math.PI);
                
                const startPos = this.getMobiusPosition(u, 0);
                const endPos = this.getMobiusPosition(u, 1);
                
                // Apply rotation using matrix
                startPos.applyMatrix4(rotationMatrix);
                endPos.applyMatrix4(rotationMatrix);
                
                // Update positions array for all lines
                positions[i] = glowPositions[i] = outerGlowPositions[i] = startPos.x;
                positions[i + 1] = glowPositions[i + 1] = outerGlowPositions[i + 1] = startPos.y;
                positions[i + 2] = glowPositions[i + 2] = outerGlowPositions[i + 2] = startPos.z;
                positions[i + 3] = glowPositions[i + 3] = outerGlowPositions[i + 3] = endPos.x;
                positions[i + 4] = glowPositions[i + 4] = outerGlowPositions[i + 4] = endPos.y;
                positions[i + 5] = glowPositions[i + 5] = outerGlowPositions[i + 5] = endPos.z;
            }
            
            line.geometry.attributes.position.needsUpdate = true;
            glowLine.geometry.attributes.position.needsUpdate = true;
            outerGlowLine.geometry.attributes.position.needsUpdate = true;
        }

        this.controls.update();
        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.render(this.scene, this.camera);
    }

    // Modified methods to control the wave
    setTrackSpeed(speedFactor) {
        this.waveConfig.trackSpeedFactor = speedFactor;
        console.log(`Track speed factor set to: ${speedFactor}`);
    }

    setWaveFrequency(frequency) {
        this.waveConfig.frequency = frequency;
        this.shaderUniforms.waveFrequency.value = frequency;
        console.log(`Wave frequency set to: ${frequency}`);
    }
}

// Initialize the application
console.log('Starting application...');
window.mobiusStrip = new MobiusStrip(); // Make it accessible globally as window.mobiusStrip 

// Set the track speed to half the default value
window.mobiusStrip.setTrackSpeed(0.30);

// Mobile Menu Functionality
document.addEventListener('DOMContentLoaded', function() {
    console.log('DOM loaded - initializing menu');
    const menuButton = document.querySelector('.mobile-menu-button');
    const mobileNav = document.querySelector('.mobile-nav');
    
    if (menuButton && mobileNav) {
        console.log('Menu elements found');
        
        // Log the button's position for debugging
        const rect = menuButton.getBoundingClientRect();
        console.log('Menu button position:', rect);
        
        menuButton.addEventListener('click', function(e) {
            console.log('Menu button clicked');
            // Prevent any event propagation
            e.preventDefault();
            e.stopPropagation();
            
            menuButton.classList.toggle('active');
            mobileNav.classList.toggle('active');
            
            console.log('Menu toggled, active:', menuButton.classList.contains('active'));
        });
        
        // Close menu when a link is clicked
        const navLinks = document.querySelectorAll('.mobile-nav-list a');
        navLinks.forEach(link => {
            link.addEventListener('click', function() {
                console.log('Menu link clicked, closing menu');
                menuButton.classList.remove('active');
                mobileNav.classList.remove('active');
            });
        });
        
        // Add a fallback close option by clicking anywhere on the page
        document.body.addEventListener('click', function(e) {
            // Only close if the menu is open and the click wasn't on the menu or button
            if (mobileNav.classList.contains('active') && 
                !mobileNav.contains(e.target) && 
                !menuButton.contains(e.target)) {
                console.log('Body clicked, closing menu');
                menuButton.classList.remove('active');
                mobileNav.classList.remove('active');
            }
        });
    } else {
        console.error('Menu elements not found. Button:', menuButton, 'Nav:', mobileNav);
    }
});

// window.mobiusStrip.setDarkGreyColor(0);
